export interface ProfileFormSection {
  index: number;
  title: string;
}

type FormLocationFieldKey =
  | 'street'
  | 'city'
  | 'region'
  | 'postal'
  | 'country'
  | 'approximate'
  | 'isPrimary';
type FormSocialProfileURLFieldKey =
  | 'social_angellist_url'
  | 'social_facebook_url'
  | 'social_flickr_url'
  | 'social_foursquare_url'
  | 'social_github_url'
  | 'social_instagram_url'
  | 'social_linkedin_url'
  | 'social_medium_url'
  | 'social_pinterest_url'
  | 'social_quora_url'
  | 'social_reddit_url'
  | 'social_slideshare_url'
  | 'social_soundcloud_url'
  | 'social_tumblr_url'
  | 'social_twitter_url'
  | 'social_vimeo_url'
  | 'social_youtube_url'
  | 'all_websites'
  | 'url'
  | 'isPrimary';

export type FormFieldKey =
  | 'first_name'
  | 'last_name'
  | 'bio'
  | 'headline'
  | 'gender'
  | 'pronouns'
  | 'age'
  | 'birthday'
  | 'send_birthday_notification'
  | 'is_memorialized'
  | 'memorialized_at'
  | 'send_memorialized_notification'
  | 'all_locations'
  | 'avatar_url'
  | FormLocationFieldKey
  | FormSocialProfileURLFieldKey;

interface TextFormField {
  type: 'text';
  key: FormFieldKey;
  label: string;
  size?: number;
  value?: string;
}

interface DropdownFormField {
  type: 'dropdown';
  key: FormFieldKey;
  label: string;
  size?: number;
  value?: string;
  options: string[];
  placeholder?: string;
  custom?: string; // label for "add your own" option
  clear?: string; // label for option that clears the field
}

interface IntegerFormField {
  type: 'integer';
  key: FormFieldKey;
  label: string;
  value?: number;
}

interface FormDateValue {
  month?: number;
  day?: number;
  year?: number;
}

interface DateFormField {
  type: 'birthdate';
  key: FormFieldKey;
  label: string;
  size?: number;
  value?: FormDateValue;
}

interface ToggleFormField {
  type: 'toggle';
  key: FormFieldKey;
  label: string;
  size?: number;
  value?: boolean;
  title?: string;
}

interface GalleryFormField {
  type: 'gallery';
  key: 'avatar_url';
  value: string;
  options: string[];
}

type SingletonFormField =
  | TextFormField
  | DropdownFormField
  | IntegerFormField
  | DateFormField
  | ToggleFormField
  | GalleryFormField;

interface RepeaterFormField {
  type: 'repeater';
  key: FormFieldKey;
  title: string;
  data: any[]; // array of items in the repeater, use generic?
  form: SingletonFormField[]; // array of form fields to use for each item in the repeater
}

export type FormField = SingletonFormField | RepeaterFormField;

export interface APIFormSection {
  label: string;
  type: 'section';
  fields: FormField[];
}

const seedRepeaterFields = (section: APIFormSection) => {
  return {
    ...section,
    fields: section.fields.map((field) => {
      if (field.type === 'repeater' && field.data && field.data.length === 0) {
        return { ...field, data: [{}] }; // ensure at least one empty object in the data array for repeaters
      }
      return field;
    }),
  };
};

export const prepareFormForView = (form: APIFormSection[]) => {
  return form.reduce(
    (
      profileData: {
        sections: ProfileFormSection[];
        avatar: GalleryFormField | undefined;
        form: APIFormSection[];
      },
      section,
      index,
    ) => {
      return {
        sections:
          section.type === 'section'
            ? [...profileData.sections, { index, title: section.label }]
            : profileData.sections,
        form: [...profileData.form, seedRepeaterFields(section)], // process each section to ensure repeaters have at least one entry
        avatar:
          profileData.avatar ||
          section.fields?.find(
            (field): field is GalleryFormField =>
              field.key === 'avatar_url' && field.type === 'gallery',
          ),
      };
    },
    { form: [] as APIFormSection[], avatar: undefined, sections: [] as ProfileFormSection[] },
  );
};

export const createValueChangeHandler =
  ({ setUserUpdates, userUpdates, formBirthdayDate, formDeceasedDate }) =>
  (key, value) => {
    const updatedData = { ...userUpdates, [key]: value };

    if (key === 'is_memorialized' && value === true) {
      return setUserUpdates({ ...updatedData, send_birthday_notification: false });
    }

    if (key === 'is_memorialized' && value === false) {
      return setUserUpdates({
        ...updatedData,
        send_memorialized_notification: false,
        memorialized_at: { month: null, day: null, year: null },
      });
    }

    if (
      key === 'send_birthday_notification' &&
      value === true &&
      (!formBirthdayDate?.month || !formBirthdayDate?.day)
    ) {
      const today = new Date();
      const unsavedBirthdayDate = userUpdates.birthday;
      const birthday = {
        month: unsavedBirthdayDate?.month || formBirthdayDate?.month || today.getMonth() + 1,
        day: unsavedBirthdayDate?.day || formBirthdayDate?.day || today.getDate(),
        year: unsavedBirthdayDate?.year || formBirthdayDate?.year,
      };
      return setUserUpdates({ ...updatedData, birthday });
    }

    if (
      key === 'send_memorialized_notification' &&
      value === true &&
      (!formDeceasedDate?.month || !formDeceasedDate?.day)
    ) {
      const today = new Date();
      const unsavedDeceasedDate = userUpdates.memorialized_at;
      const memorialized_at = {
        month: unsavedDeceasedDate?.month || formDeceasedDate?.month || today.getMonth() + 1,
        day: unsavedDeceasedDate?.day || formDeceasedDate?.day || today.getDate(),
        year: unsavedDeceasedDate?.year || formDeceasedDate?.year,
      };
      return setUserUpdates({ ...updatedData, memorialized_at });
    }

    setUserUpdates(updatedData);
  };
